// frontend/src/modules/notas/pages/NotasFiscais.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFilePdf, 
  FaEye, 
  FaQrcode, 
  FaBarcode, 
  FaCheckCircle, 
  FaTimes, 
  FaPrint, 
  FaDownload, 
  FaEnvelope, 
  FaWhatsapp, 
  FaFileInvoice, 
  FaSpinner, 
  FaRegFilePdf, 
  FaRegFileAlt, 
  FaRegFile,
  FaAngleDown, 
  FaAngleUp, 
  FaSync
} from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

function NotasFiscais({ usuario }) {
  const empresaId = usuario?.empresa_id || 1;
  const token = localStorage.getItem('token');
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const printRef = useRef();

  // ============================================
  // STATES
  // ============================================
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [notaSelecionada, setNotaSelecionada] = useState(null);
  const [mostrarModalVisualizar, setMostrarModalVisualizar] = useState(false);
  const [pixGerado, setPixGerado] = useState(null);
  const [boletoGerado, setBoletoGerado] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('NFE');
  const [formData, setFormData] = useState({
    chave_acesso: '',
    data_emissao: new Date().toISOString().split('T')[0],
    data_vencimento: '',
    valor: '',
    tipo: 'SAIDA',
    status: 'PENDENTE',
    forma_pagamento: '',
    cliente_fornecedor: '',
    cnpj: '',
    observacoes: '',
    tipo_documento: 'NFE',
    natureza_operacao: 'VENDA',
    cfop: '5102',
    ncm: '8471.50.10',
    cst: '00',
    csosn: '101',
    aliquota_icms: '18.00',
    aliquota_pis: '1.65',
    aliquota_cofins: '7.60'
  });

  // ============================================
  // FUNÇÕES AUXILIARES
  // ============================================
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  // ============================================
  // BUSCAR NOTAS
  // ============================================
  const fetchNotas = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/notas/empresa/${empresaId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Notas fiscais:', data.length);
      setNotas(data);
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BUSCAR PRÓXIMO NÚMERO DA NF-e
  // ============================================
  const buscarProximoNumero = async () => {
    try {
      const response = await fetch(`/api/notas/proximo-numero/${empresaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar próximo número');
      const data = await response.json();
      return data.proximo_numero;
    } catch (err) {
      console.error('Erro ao buscar próximo número:', err);
      return null;
    }
  };

  // ============================================
  // EFFECT
  // ============================================
  useEffect(() => {
    fetchNotas();
  }, [empresaId]);

  // ============================================
  // HANDLE CHANGE
  // ============================================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ============================================
  // CRIAR NOTA
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Buscar próximo número da NF-e
      const proximoNumero = await buscarProximoNumero();
      if (!proximoNumero) {
        throw new Error('Não foi possível obter o próximo número da NF-e');
      }

      // Buscar série configurada para a empresa
      const serieResponse = await fetch(`/api/empresas/${empresaId}/serie-nfe`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const serieData = await serieResponse.json();
      const serie = serieData.serie || '1';

      const response = await fetch(`/api/notas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          numero: proximoNumero,
          serie: serie,
          empresa_id: empresaId,
          valor: parseFloat(formData.valor) || 0
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao criar nota');
      }

      setSuccess('✅ Nota fiscal criada com sucesso!');
      setMostrarForm(false);
      setShowDropdown(false);
      setFormData({
        chave_acesso: '',
        data_emissao: new Date().toISOString().split('T')[0],
        data_vencimento: '',
        valor: '',
        tipo: 'SAIDA',
        status: 'PENDENTE',
        forma_pagamento: '',
        cliente_fornecedor: '',
        cnpj: '',
        observacoes: '',
        tipo_documento: 'NFE',
        natureza_operacao: 'VENDA',
        cfop: '5102',
        ncm: '8471.50.10',
        cst: '00',
        csosn: '101',
        aliquota_icms: '18.00',
        aliquota_pis: '1.65',
        aliquota_cofins: '7.60'
      });
      fetchNotas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EMITIR NOTA FISCAL (NF-e)
  // ============================================
  const handleEmitirNFe = async (notaId) => {
    if (!window.confirm('Confirmar emissão da Nota Fiscal Eletrônica?')) return;
    
    setLoadingAction(true);
    setError('');
    setSuccess('');

    try {
      const nota = notas.find(n => n.id === notaId);
      if (!nota) throw new Error('Nota não encontrada');

      const providerResponse = await fetch('/tax/providers/active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!providerResponse.ok) {
        throw new Error('Erro ao buscar provedores');
      }
      
      const providers = await providerResponse.json();
      const provider = providers.find(p => p.tipo === 'FEDERAL' && p.ativo);

      if (!provider) {
        throw new Error('Nenhum provedor de NF-e ativo configurado. Acesse Configuração Tributária para configurar.');
      }

      const dadosEmissao = {
        numero: nota.numero,
        serie: nota.serie || '1',
        data_emissao: nota.data_emissao,
        data_vencimento: nota.data_vencimento,
        natureza_operacao: 'VENDA',
        empresa_id: empresaId,
        provider_id: provider.id,
        cliente: {
          cnpj: nota.cnpj || '00000000000000',
          razao_social: nota.cliente_fornecedor || 'Cliente não informado',
          uf: 'SP'
        },
        itens: [
          {
            descricao: 'Produto/Serviço',
            quantidade: 1,
            valor_unitario: parseFloat(nota.valor) || 0,
            ncm: '8471.50.10'
          }
        ]
      };

      const response = await fetch('/api/nfe/emitir', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dadosEmissao)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao emitir NF-e');
      }

      const resultado = await response.json();
      
      setSuccess(`✅ NF-e emitida com sucesso! Chave: ${resultado.nota?.chave_acesso || 'gerada'}`);
      setMostrarModalVisualizar(false);
      fetchNotas();
      
      if (resultado.danfe_pdf) {
        setTimeout(() => {
          if (window.confirm('DANFE gerado com sucesso! Deseja baixar agora?')) {
            window.open(`/api/nfe/${resultado.nota?.id}/danfe`, '_blank');
          }
        }, 1000);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // CONSULTAR NF-e
  // ============================================
  const handleConsultarNFe = async (chave) => {
    if (!chave) return;
    
    setLoadingAction(true);
    try {
      const response = await fetch(`/api/nfe/consultar/${chave}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erro ao consultar NF-e');
      
      const data = await response.json();
      alert(`Status da NF-e: ${data.status}\nChave: ${data.chave}`);
      
      if (data.danfe) {
        await fetch(`/api/nfe/${data.id}/atualizar-danfe`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ danfe_pdf: data.danfe })
        });
        fetchNotas();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // VISUALIZAR NOTA
  // ============================================
  const handleVisualizar = (nota) => {
    setNotaSelecionada(nota);
    setMostrarModalVisualizar(true);
  };

  // ============================================
  // GERAR PIX
  // ============================================
  const handleGerarPIX = async (id) => {
    setLoadingAction(true);
    try {
      const response = await fetch(`/api/notas/${id}/gerar-pix`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao gerar PIX');
      const data = await response.json();
      setPixGerado(data.pix || data);
      setSuccess('✅ PIX gerado com sucesso!');
      fetchNotas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // GERAR BOLETO
  // ============================================
  const handleGerarBoleto = async (id) => {
    setLoadingAction(true);
    try {
      const response = await fetch(`/api/notas/${id}/gerar-boleto`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao gerar boleto');
      const data = await response.json();
      setBoletoGerado(data.boleto || data);
      setSuccess('✅ Boleto gerado com sucesso!');
      fetchNotas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // CONFIRMAR PAGAMENTO
  // ============================================
  const handleConfirmarPagamento = async (id) => {
    if (!window.confirm('Confirmar pagamento desta nota fiscal?')) return;
    setLoadingAction(true);
    try {
      const response = await fetch(`/api/notas/${id}/confirmar-pagamento`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status_pagamento: 'PAGO',
          data_pagamento: new Date().toISOString().split('T')[0]
        })
      });
      if (!response.ok) throw new Error('Erro ao confirmar pagamento');
      setSuccess('✅ Pagamento confirmado!');
      fetchNotas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // EXCLUIR NOTA
  // ============================================
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta nota fiscal?')) return;
    setLoadingAction(true);
    try {
      const response = await fetch(`/api/notas/${id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao excluir nota');
      setSuccess('✅ Nota fiscal excluída!');
      fetchNotas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // ============================================
  // IMPRIMIR NOTA
  // ============================================
  const handleImprimir = () => {
    if (!notaSelecionada) return;
    
    const conteudoImpressao = `
      <html>
        <head>
          <title>Nota Fiscal ${notaSelecionada.numero}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
            .header h1 { margin: 0; color: #007bff; }
            .header p { margin: 5px 0; color: #666; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
            .info-item { padding: 8px; border-bottom: 1px solid #eee; }
            .info-item strong { display: inline-block; width: 150px; color: #555; }
            .total { text-align: right; font-size: 24px; font-weight: bold; color: #007bff; margin: 20px 0; }
            .footer { text-align: center; border-top: 2px solid #333; padding-top: 20px; margin-top: 20px; color: #666; font-size: 12px; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; background: #d4edda; color: #155724; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📄 NOTA FISCAL</h1>
            <p>Nº ${notaSelecionada.numero} - Série ${notaSelecionada.serie || '-'}</p>
            <p>Emitida em: ${formatDate(notaSelecionada.data_emissao)}</p>
          </div>
          
          <div class="info-grid">
            <div class="info-item"><strong>Cliente/Fornecedor:</strong> ${notaSelecionada.cliente_fornecedor || '-'}</div>
            <div class="info-item"><strong>CNPJ:</strong> ${notaSelecionada.cnpj || '-'}</div>
            <div class="info-item"><strong>Tipo:</strong> ${notaSelecionada.tipo === 'SAIDA' ? '📤 Saída' : '📥 Entrada'}</div>
            <div class="info-item"><strong>Forma Pagamento:</strong> ${notaSelecionada.forma_pagamento || '-'}</div>
            <div class="info-item"><strong>Data Vencimento:</strong> ${formatDate(notaSelecionada.data_vencimento)}</div>
            <div class="info-item"><strong>Status:</strong> <span class="status-badge">${notaSelecionada.status || 'PENDENTE'}</span></div>
            <div class="info-item"><strong>Pagamento:</strong> ${notaSelecionada.status_pagamento || 'PENDENTE'}</div>
            <div class="info-item"><strong>Chave Acesso:</strong> ${notaSelecionada.chave_acesso || '-'}</div>
          </div>
          
          <div class="total">
            Valor: ${formatCurrency(notaSelecionada.valor)}
          </div>
          
          ${notaSelecionada.observacoes ? `<div style="margin: 20px 0;"><strong>Observações:</strong> ${notaSelecionada.observacoes}</div>` : ''}
          
          <div class="footer">
            <p>Documento emitido eletronicamente - ${new Date().toLocaleString('pt-BR')}</p>
          </div>
        </body>
      </html>
    `;

    const janelaImpressao = window.open('', '_blank', 'width=800,height=600');
    janelaImpressao.document.write(conteudoImpressao);
    janelaImpressao.document.close();
    janelaImpressao.focus();
    janelaImpressao.print();
  };

  // ============================================
  // BAIXAR PDF
  // ============================================
  const handleBaixarPDF = () => {
    if (!notaSelecionada) return;
    
    const conteudoPDF = `
      <html>
        <head>
          <title>Nota_Fiscal_${notaSelecionada.numero}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
            .header h1 { margin: 0; color: #007bff; }
            .header p { margin: 5px 0; color: #666; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
            .info-item { padding: 8px; border-bottom: 1px solid #eee; }
            .info-item strong { display: inline-block; width: 150px; color: #555; }
            .total { text-align: right; font-size: 24px; font-weight: bold; color: #007bff; margin: 20px 0; }
            .footer { text-align: center; border-top: 2px solid #333; padding-top: 20px; margin-top: 20px; color: #666; font-size: 12px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📄 NOTA FISCAL</h1>
            <p>Nº ${notaSelecionada.numero} - Série ${notaSelecionada.serie || '-'}</p>
            <p>Emitida em: ${formatDate(notaSelecionada.data_emissao)}</p>
          </div>
          
          <div class="info-grid">
            <div class="info-item"><strong>Cliente/Fornecedor:</strong> ${notaSelecionada.cliente_fornecedor || '-'}</div>
            <div class="info-item"><strong>CNPJ:</strong> ${notaSelecionada.cnpj || '-'}</div>
            <div class="info-item"><strong>Tipo:</strong> ${notaSelecionada.tipo === 'SAIDA' ? '📤 Saída' : '📥 Entrada'}</div>
            <div class="info-item"><strong>Forma Pagamento:</strong> ${notaSelecionada.forma_pagamento || '-'}</div>
            <div class="info-item"><strong>Data Vencimento:</strong> ${formatDate(notaSelecionada.data_vencimento)}</div>
            <div class="info-item"><strong>Status:</strong> ${notaSelecionada.status || 'PENDENTE'}</div>
            <div class="info-item"><strong>Pagamento:</strong> ${notaSelecionada.status_pagamento || 'PENDENTE'}</div>
            <div class="info-item"><strong>Chave Acesso:</strong> ${notaSelecionada.chave_acesso || '-'}</div>
          </div>
          
          <div class="total">
            Valor: ${formatCurrency(notaSelecionada.valor)}
          </div>
          
          ${notaSelecionada.observacoes ? `<div style="margin: 20px 0;"><strong>Observações:</strong> ${notaSelecionada.observacoes}</div>` : ''}
          
          <div class="footer">
            <p>Documento emitido eletronicamente - ${new Date().toLocaleString('pt-BR')}</p>
          </div>
        </body>
      </html>
    `;

    const janelaPDF = window.open('', '_blank', 'width=800,height=600');
    janelaPDF.document.write(conteudoPDF);
    janelaPDF.document.close();
    janelaPDF.focus();
    
    janelaPDF.onload = function() {
      janelaPDF.print();
    };
  };

  // ============================================
  // ENVIAR POR EMAIL
  // ============================================
  const handleEnviarEmail = () => {
    if (!notaSelecionada) return;
    
    const assunto = `Nota Fiscal Nº ${notaSelecionada.numero}`;
    const corpo = `
      Prezado(a),

      Segue em anexo a Nota Fiscal Nº ${notaSelecionada.numero}.

      Dados da Nota:
      - Número: ${notaSelecionada.numero}
      - Série: ${notaSelecionada.serie || '-'}
      - Data Emissão: ${formatDate(notaSelecionada.data_emissao)}
      - Data Vencimento: ${formatDate(notaSelecionada.data_vencimento)}
      - Valor: ${formatCurrency(notaSelecionada.valor)}
      - Cliente/Fornecedor: ${notaSelecionada.cliente_fornecedor || '-'}
      - Status: ${notaSelecionada.status || 'PENDENTE'}
      - Pagamento: ${notaSelecionada.status_pagamento || 'PENDENTE'}

      ${notaSelecionada.observacoes ? `Observações: ${notaSelecionada.observacoes}` : ''}

      Atenciosamente,
      Sistema de Notas Fiscais
    `;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
    window.open(mailtoLink, '_blank');
  };

  // ============================================
  // ENVIAR POR WHATSAPP
  // ============================================
  const handleEnviarWhatsApp = () => {
    if (!notaSelecionada) return;
    
    const mensagem = `
      *📄 NOTA FISCAL Nº ${notaSelecionada.numero}*

      *Cliente/Fornecedor:* ${notaSelecionada.cliente_fornecedor || '-'}
      *Valor:* ${formatCurrency(notaSelecionada.valor)}
      *Data Emissão:* ${formatDate(notaSelecionada.data_emissao)}
      *Data Vencimento:* ${formatDate(notaSelecionada.data_vencimento)}
      *Status:* ${notaSelecionada.status || 'PENDENTE'}
      *Pagamento:* ${notaSelecionada.status_pagamento || 'PENDENTE'}
      ${notaSelecionada.observacoes ? `\n*Observações:* ${notaSelecionada.observacoes}` : ''}

      Enviado pelo Sistema de Notas Fiscais
    `;

    const numeroWhatsApp = '';
    const urlWhatsApp = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensagem)}`;
    window.open(urlWhatsApp, '_blank');
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '50px', fontSize: isMobile ? '14px' : '18px' }}>
      🔄 Carregando notas fiscais...
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho com Dropdown */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>📄 Notas Fiscais</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Gerencie notas fiscais de entrada e saída
          </p>
        </div>
        
        <div style={{ position: 'relative', display: 'inline-block', width: isMobile ? '100%' : 'auto' }}>
          <button 
            onClick={() => {
              if (mostrarForm) {
                setMostrarForm(false);
                setShowDropdown(false);
              } else {
                setShowDropdown(!showDropdown);
              }
            }} 
            style={{ 
              padding: isMobile ? '8px 16px' : '10px 20px', 
              backgroundColor: mostrarForm ? '#dc3545' : '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              fontSize: isMobile ? '13px' : '14px',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            {mostrarForm ? <><FaTimes /> Cancelar</> : <><FaPlus /> Nova Nota {showDropdown ? <FaAngleUp /> : <FaAngleDown />}</>}
          </button>
          
          {!mostrarForm && showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '5px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: isMobile ? '100%' : '220px',
              zIndex: 1000,
              overflow: 'hidden',
              border: '1px solid #e0e0e0'
            }}>
              <button
                onClick={() => { 
                  setTipoDocumento('NFE');
                  setMostrarForm(true);
                  setShowDropdown(false);
                  setFormData({ ...formData, tipo_documento: 'NFE' });
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <span style={{ fontSize: '20px' }}>📄</span>
                <div>
                  <div style={{ fontWeight: 'bold' }}>NF-e</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Nota Fiscal Eletrônica</div>
                </div>
              </button>
              
              <button
                onClick={() => { 
                  setTipoDocumento('NFCE');
                  setMostrarForm(true);
                  setShowDropdown(false);
                  setFormData({ ...formData, tipo_documento: 'NFCE' });
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <span style={{ fontSize: '20px' }}>🧾</span>
                <div>
                  <div style={{ fontWeight: 'bold' }}>NFC-e</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Nota Fiscal de Consumidor</div>
                </div>
              </button>
              
              <button
                onClick={() => { 
                  setTipoDocumento('CTE');
                  setMostrarForm(true);
                  setShowDropdown(false);
                  setFormData({ ...formData, tipo_documento: 'CTE' });
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <span style={{ fontSize: '20px' }}>🚛</span>
                <div>
                  <div style={{ fontWeight: 'bold' }}>CT-e</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Conhecimento de Transporte</div>
                </div>
              </button>
              
              <button
                onClick={() => { 
                  setTipoDocumento('NFS');
                  setMostrarForm(true);
                  setShowDropdown(false);
                  setFormData({ ...formData, tipo_documento: 'NFS' });
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <span style={{ fontSize: '20px' }}>🏢</span>
                <div>
                  <div style={{ fontWeight: 'bold' }}>NFS-e</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Nota Fiscal de Serviços</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          padding: isMobile ? '10px 15px' : '12px 20px', 
          borderRadius: '4px', 
          color: '#c62828', 
          marginBottom: '15px', 
          border: '1px solid #ffcdd2', 
          fontSize: isMobile ? '13px' : '14px' 
        }}>
          ❌ {error}
          <button 
            onClick={fetchNotas}
            style={{ 
              marginLeft: '15px', 
              padding: '4px 12px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '13px'
            }}
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {success && (
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          padding: isMobile ? '10px 15px' : '12px 20px', 
          borderRadius: '4px', 
          color: '#2e7d32', 
          marginBottom: '15px', 
          border: '1px solid #c8e6c9', 
          fontSize: isMobile ? '13px' : '14px' 
        }}>
          {success}
        </div>
      )}

      {/* Formulário */}
      {mostrarForm && (
        <form onSubmit={handleSubmit} style={{ 
          backgroundColor: '#f8f9fa', 
          padding: isMobile ? '15px' : '20px', 
          borderRadius: '8px', 
          marginBottom: '20px', 
          border: '1px solid #dee2e6' 
        }}>
          <h3 style={{ marginTop: 0, fontSize: isMobile ? '16px' : '18px' }}>
            {tipoDocumento === 'NFE' && '📄 Nova NF-e'}
            {tipoDocumento === 'NFCE' && '🧾 Nova NFC-e'}
            {tipoDocumento === 'CTE' && '🚛 Novo CT-e'}
            {tipoDocumento === 'NFS' && '🏢 Nova NFS-e'}
          </h3>
          
          <div style={{ 
            backgroundColor: '#e9ecef', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '15px',
            fontSize: isMobile ? '13px' : '14px'
          }}>
            <strong>ℹ️ Informação:</strong> O número da NF-e será gerado automaticamente pela SEFAZ. 
            A série é definida nas configurações da sua empresa.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '15px' }}>
            {/* REMOVIDO: campos Número e Série */}
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Data Emissão *</label>
              <input 
                name="data_emissao" 
                value={formData.data_emissao} 
                onChange={handleChange} 
                type="date" 
                required 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Data Vencimento</label>
              <input 
                name="data_vencimento" 
                value={formData.data_vencimento} 
                onChange={handleChange} 
                type="date" 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Valor *</label>
              <input 
                name="valor" 
                value={formData.valor} 
                onChange={handleChange} 
                type="number" 
                step="0.01" 
                required 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Natureza Operação</label>
              <select 
                name="natureza_operacao" 
                value={formData.natureza_operacao} 
                onChange={handleChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }}
              >
                <option value="VENDA">Venda</option>
                <option value="DEVOLUCAO">Devolução</option>
                <option value="BONIFICACAO">Bonificação</option>
                <option value="REMESSA">Remessa</option>
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>CFOP</label>
              <select 
                name="cfop" 
                value={formData.cfop} 
                onChange={handleChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }}
              >
                <option value="5102">5102 - Venda dentro do estado</option>
                <option value="6102">6102 - Venda fora do estado</option>
                <option value="5101">5101 - Venda produção própria</option>
                <option value="5202">5202 - Devolução</option>
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>NCM</label>
              <input 
                name="ncm" 
                value={formData.ncm} 
                onChange={handleChange}
                placeholder="Ex: 8471.50.10"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>CST ICMS</label>
              <select 
                name="cst" 
                value={formData.cst} 
                onChange={handleChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }}
              >
                <option value="00">00 - Tributada</option>
                <option value="10">10 - Tributada com isenção</option>
                <option value="20">20 - Com redução</option>
                <option value="40">40 - Isenta</option>
                <option value="41">41 - Não tributada</option>
                <option value="60">60 - Imune</option>
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Alíquota ICMS (%)</label>
              <input 
                name="aliquota_icms" 
                value={formData.aliquota_icms} 
                onChange={handleChange}
                type="number"
                step="0.01"
                placeholder="18.00"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Forma Pagamento</label>
              <select 
                name="forma_pagamento" 
                value={formData.forma_pagamento} 
                onChange={handleChange} 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }}
              >
                <option value="">Selecione...</option>
                <option value="PIX">📱 PIX</option>
                <option value="BOLETO">📄 Boleto</option>
                <option value="CARTAO">💳 Cartão</option>
                <option value="DINHEIRO">💵 Dinheiro</option>
                <option value="TED">🏦 TED</option>
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Tipo</label>
              <select 
                name="tipo" 
                value={formData.tipo} 
                onChange={handleChange} 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }}
              >
                <option value="SAIDA">📤 Saída</option>
                <option value="ENTRADA">📥 Entrada</option>
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Cliente/Fornecedor *</label>
              <input 
                name="cliente_fornecedor" 
                value={formData.cliente_fornecedor} 
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>CNPJ/CPF *</label>
              <input 
                name="cnpj" 
                value={formData.cnpj} 
                onChange={handleChange}
                required
                placeholder="00.000.000/0000-00"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
            
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Observações</label>
              <textarea 
                name="observacoes" 
                value={formData.observacoes} 
                onChange={handleChange} 
                rows="2" 
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px', boxSizing: 'border-box' }} 
              />
            </div>
          </div>
          <button 
            type="submit" 
            disabled={loading} 
            style={{ 
              marginTop: '15px', 
              padding: isMobile ? '8px 20px' : '10px 30px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            {loading ? 'Salvando...' : '💾 Salvar Nota'}
          </button>
        </form>
      )}

      {/* Continua com o resto do código (Modais, Lista de Notas, etc.) */}
      {/* ... resto do código que já estava funcionando ... */}
    </div>
  );
}

export default NotasFiscais;